import React, { createContext, useCallback, useContext, useState } from 'react';

// TSP Information with ID mapping
export interface TSPInfo {
  axle_tsp_id: number;
  tsp_source: string; // The TSP key/name
  invalid_credentials?: boolean;
}

// Application State Interface (non-JWT state only)
export interface AppState {
  // Service configuration
  serviceName: string;
  tspCount: number;
  hcssClientID: string;
  motiveClientID: string;
  bouncieClientID: string;
  samsaraClientID: string;
  zubieClientID: string;

  // TSP management
  TSP: string;
  existingTSPs: string[]; // Legacy: TSP keys only
  existingTSPsWithIDs: TSPInfo[]; // New: TSP info with IDs
  updatingTSP: boolean;

  // Flow state
  approved: boolean;
  onboarded: boolean;
  magicLink: boolean;

  // Legacy redirect URIs (these should come from JWT, but keeping for compatibility)
  redirectURI: string;
  redirectURISuccess: string;
  redirectURIError: string;

  // For compatibility with main branch
  serviceID: string;
}

// Application Actions Interface
export interface AppActions {
  setServiceName: (name: string) => void;
  setTspCount: (count: number) => void;
  setHCSSClientID: (id: string) => void;
  setMotiveClientID: (id: string) => void;
  setBouncieClientID: (id: string) => void;
  setSamsaraClientID: (id: string) => void;
  setZubieClientID: (id: string) => void;
  setTSP: (tsp: string) => void;
  setExistingTSPs: (tsps: string[]) => void;
  setExistingTSPsWithIDs: (tsps: TSPInfo[]) => void;
  setUpdatingTSP: (updating: boolean) => void;
  setApproved: (approved: boolean) => void;
  setOnboarded: (onboarded: boolean) => void;
  setMagicLink: (magicLink: boolean) => void;
  setRedirectURI: (uri: string) => void;
  setRedirectURISuccess: (uri: string) => void;
  setRedirectURIError: (uri: string) => void;
  // Helper function to get TSP ID by key
  getTSPIDByKey: (tspKey: string) => number | null;
}

// Combined Context Interface
export interface AppContextValue extends AppState, AppActions {}

// Create Context
const AppContext = createContext<AppContextValue | null>(null);

// App State Provider Component
export const AppStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Service configuration
  const [serviceName, setServiceName] = useState('');
  const [tspCount, setTspCount] = useState(0);
  const [hcssClientID, setHCSSClientID] = useState('');
  const [motiveClientID, setMotiveClientID] = useState('');
  const [bouncieClientID, setBouncieClientID] = useState('');
  const [samsaraClientID, setSamsaraClientID] = useState('');
  const [zubieClientID, setZubieClientID] = useState('');

  // TSP management
  const [TSP, setTSP] = useState('');
  const [existingTSPs, setExistingTSPs] = useState<string[]>([]);
  const [existingTSPsWithIDs, setExistingTSPsWithIDs] = useState<TSPInfo[]>([]);
  const [updatingTSP, setUpdatingTSP] = useState(false);

  // Flow state
  const [approved, setApproved] = useState(false);
  const [onboarded, setOnboarded] = useState(false);
  const [magicLink, setMagicLink] = useState(false);

  // Legacy redirect URIs (keeping for compatibility, but JWT should be source of truth)
  const [redirectURI, setRedirectURI] = useState('');
  const [redirectURISuccess, setRedirectURISuccess] = useState('');
  const [redirectURIError, setRedirectURIError] = useState('');

  // For compatibility with main branch - derive from serviceName or could be set separately
  const serviceID = serviceName; // Simple mapping for now

  // Helper function to get TSP ID by key
  const getTSPIDByKey = useCallback(
    (tspKey: string): number | null => {
      const tspInfo = existingTSPsWithIDs.find(
        (tsp) => tsp.tsp_source === tspKey
      );
      return tspInfo ? tspInfo.axle_tsp_id : null;
    },
    [existingTSPsWithIDs]
  );

  // Wrap setters in useCallback for performance
  const setServiceNameCb = useCallback(
    (name: string) => setServiceName(name),
    []
  );
  const setTspCountCb = useCallback((count: number) => {
    setTspCount(count);
    // Store TSP count in localStorage for URL utils
    try {
      localStorage.setItem('tsp_count', count.toString());
    } catch (error) {
      console.warn(
        '[AppStateProvider] Failed to store TSP count in localStorage:',
        error
      );
    }
  }, []);
  const setHCSSClientIDCb = useCallback(
    (id: string) => setHCSSClientID(id),
    []
  );
  const setMotiveClientIDCb = useCallback(
    (id: string) => setMotiveClientID(id),
    []
  );
  const setBouncieClientIDCb = useCallback(
    (id: string) => setBouncieClientID(id),
    []
  );
  const setSamsaraClientIDCb = useCallback(
    (id: string) => setSamsaraClientID(id),
    []
  );
  const setZubieClientIDCb = useCallback(
    (id: string) => setZubieClientID(id),
    []
  );
  const setTSPCb = useCallback((tsp: string) => setTSP(tsp), []);
  const setExistingTSPsCb = useCallback(
    (tsps: string[]) => setExistingTSPs(tsps),
    []
  );
  const setExistingTSPsWithIDsCb = useCallback((tsps: TSPInfo[]) => {
    setExistingTSPsWithIDs(tsps);
    // Also update the legacy existingTSPs array for backward compatibility
    const tspKeys = tsps.map((tsp) => tsp.tsp_source);
    setExistingTSPs(tspKeys);
  }, []);
  const setUpdatingTSPCb = useCallback(
    (updating: boolean) => setUpdatingTSP(updating),
    []
  );
  const setApprovedCb = useCallback(
    (approved: boolean) => setApproved(approved),
    []
  );
  const setOnboardedCb = useCallback(
    (onboarded: boolean) => setOnboarded(onboarded),
    []
  );
  const setMagicLinkCb = useCallback(
    (magicLink: boolean) => setMagicLink(magicLink),
    []
  );
  const setRedirectURICb = useCallback(
    (uri: string) => setRedirectURI(uri),
    []
  );
  const setRedirectURISuccessCb = useCallback(
    (uri: string) => setRedirectURISuccess(uri),
    []
  );
  const setRedirectURIErrorCb = useCallback(
    (uri: string) => setRedirectURIError(uri),
    []
  );

  // Context value
  const contextValue: AppContextValue = {
    // State
    serviceName,
    tspCount,
    hcssClientID,
    motiveClientID,
    bouncieClientID,
    samsaraClientID,
    zubieClientID,
    TSP,
    existingTSPs,
    existingTSPsWithIDs,
    updatingTSP,
    approved,
    onboarded,
    magicLink,
    redirectURI,
    redirectURISuccess,
    redirectURIError,
    serviceID,

    // Actions
    setServiceName: setServiceNameCb,
    setTspCount: setTspCountCb,
    setHCSSClientID: setHCSSClientIDCb,
    setMotiveClientID: setMotiveClientIDCb,
    setBouncieClientID: setBouncieClientIDCb,
    setSamsaraClientID: setSamsaraClientIDCb,
    setZubieClientID: setZubieClientIDCb,
    setTSP: setTSPCb,
    setExistingTSPs: setExistingTSPsCb,
    setExistingTSPsWithIDs: setExistingTSPsWithIDsCb,
    setUpdatingTSP: setUpdatingTSPCb,
    setApproved: setApprovedCb,
    setOnboarded: setOnboardedCb,
    setMagicLink: setMagicLinkCb,
    setRedirectURI: setRedirectURICb,
    setRedirectURISuccess: setRedirectURISuccessCb,
    setRedirectURIError: setRedirectURIErrorCb,
    getTSPIDByKey,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// Hook to use App context
export const useAppState = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

// Convenience hooks for specific app state (to minimize re-renders)
export const useServiceConfig = () => {
  const {
    serviceName,
    hcssClientID,
    motiveClientID,
    bouncieClientID,
    samsaraClientID,
    zubieClientID,
    setServiceName,
    setHCSSClientID,
    setMotiveClientID,
    setBouncieClientID,
    setSamsaraClientID,
    setZubieClientID,
  } = useAppState();

  return {
    serviceName,
    hcssClientID,
    motiveClientID,
    bouncieClientID,
    samsaraClientID,
    zubieClientID,
    setServiceName,
    setHCSSClientID,
    setMotiveClientID,
    setBouncieClientID,
    setSamsaraClientID,
    setZubieClientID,
  };
};

export const useTSPState = () => {
  const {
    TSP,
    existingTSPs,
    existingTSPsWithIDs,
    updatingTSP,
    setTSP,
    setExistingTSPs,
    setExistingTSPsWithIDs,
    setUpdatingTSP,
    getTSPIDByKey,
  } = useAppState();
  return {
    TSP,
    existingTSPs,
    existingTSPsWithIDs,
    updatingTSP,
    setTSP,
    setExistingTSPs,
    setExistingTSPsWithIDs,
    setUpdatingTSP,
    getTSPIDByKey,
  };
};

export const useFlowState = () => {
  const {
    approved,
    onboarded,
    magicLink,
    setApproved,
    setOnboarded,
    setMagicLink,
  } = useAppState();
  return {
    approved,
    onboarded,
    magicLink,
    setApproved,
    setOnboarded,
    setMagicLink,
  };
};
